/**
 * RecipeCorpusPage — Story 6.7: Recipe Corpus Admin Editor
 *
 * Full CRUD feature for managing the clinical recipe corpus.
 * Requires staff:admin role (enforced at route level in app.tsx).
 *
 * Replaces the read-only stub from Story 6.3.
 *
 * Features:
 *   - Managed list: title, clinical tags, language, status badge
 *   - Filterable by status (draft/published/all)
 *   - Searchable by title (client-side)
 *   - Each row opens a full editor
 *   - "New Recipe" action opens create form
 *   - Full field editor: all Recipe fields editable
 *   - Publish workflow: draft → published
 *   - Spanish dual sign-off gate before publish
 *   - Status badge updates immediately after publish
 *
 * @NonPhiEndpoint — recipe corpus is clinical content, not member PHI.
 */

import { usePortalAuth } from '@nrx/auth-ui';
import { Table } from '@nrx/ui';
import type { JSX } from 'react';
import { useEffect, useState } from 'react';

// ─── Constants ─────────────────────────────────────────────────────────────────

import { SERVICES_BASE_URL } from '../../lib/services-base.js';

const CLINICAL_TRACK_OPTIONS = ['eCKM', 'CKM'] as const;
const DIETARY_TAG_OPTIONS = [
  'gluten-free',
  'dairy-free',
  'vegetarian',
  'vegan',
  'low-sodium',
  'low-potassium',
  'nut-free',
];
const CONDITION_TAG_OPTIONS = ['diabetes', 'hypertension', 'ckd', 'heart-disease', 'obesity'];
const CULTURAL_TAG_OPTIONS = [
  'Mediterranean',
  'Latin',
  'American',
  'Asian',
  'Middle-Eastern',
  'African',
];

// ─── Types ────────────────────────────────────────────────────────────────────

type RecipeStatus = 'draft' | 'published';
type LanguageCode = 'en' | 'es';
type ClinicalTrack = 'eCKM' | 'CKM';

interface RecipeIngredientForm {
  name: string;
  quantity: number;
  unit: string;
  sortOrder: number;
}

interface RecipeNutritionForm {
  caloriesKcal: number;
  proteinG: number;
  carbsG: number;
  fatG: number;
  sodiumMg: number;
  potassiumMg: number;
  fiberG: number;
}

interface RecipeDto {
  id: string;
  title: string;
  description?: string | null;
  cuisine?: string | null;
  prepTimeMin: number;
  cookTimeMin: number;
  servings: number;
  clinicalTags: ClinicalTrack[];
  dietaryTags: string[];
  conditionTags: string[];
  culturalTags: string[];
  language: LanguageCode;
  parentRecipeId?: string | null;
  status: RecipeStatus;
  ingredients: Array<{
    id?: string;
    name: string;
    quantity: number;
    unit: string;
    sortOrder: number;
  }>;
  instructions: string[];
  nutrition: RecipeNutritionForm | Record<string, number>;
}

interface RecipeFormState {
  title: string;
  description: string;
  cuisine: string;
  prepTimeMin: number;
  cookTimeMin: number;
  servings: number;
  language: LanguageCode;
  parentRecipeId: string;
  clinicalTags: ClinicalTrack[];
  dietaryTags: string[];
  conditionTags: string[];
  culturalTags: string[];
  ingredients: RecipeIngredientForm[];
  instructions: string[];
  nutrition: RecipeNutritionForm;
}

// ─── Helpers ──────────────────────────────────────────────────────────────────

function emptyForm(): RecipeFormState {
  return {
    title: '',
    description: '',
    cuisine: '',
    prepTimeMin: 0,
    cookTimeMin: 0,
    servings: 2,
    language: 'en',
    parentRecipeId: '',
    clinicalTags: [],
    dietaryTags: [],
    conditionTags: [],
    culturalTags: [],
    ingredients: [{ name: '', quantity: 1, unit: '', sortOrder: 0 }],
    instructions: [''],
    nutrition: {
      caloriesKcal: 0,
      proteinG: 0,
      carbsG: 0,
      fatG: 0,
      sodiumMg: 0,
      potassiumMg: 0,
      fiberG: 0,
    },
  };
}

function recipeToForm(recipe: RecipeDto): RecipeFormState {
  const rawNutrition = recipe.nutrition as Partial<RecipeNutritionForm>;
  return {
    title: recipe.title,
    description: recipe.description ?? '',
    cuisine: recipe.cuisine ?? '',
    prepTimeMin: recipe.prepTimeMin,
    cookTimeMin: recipe.cookTimeMin,
    servings: recipe.servings,
    language: recipe.language,
    parentRecipeId: recipe.parentRecipeId ?? '',
    clinicalTags: [...recipe.clinicalTags],
    dietaryTags: [...recipe.dietaryTags],
    conditionTags: [...recipe.conditionTags],
    culturalTags: [...recipe.culturalTags],
    ingredients: recipe.ingredients.map((ing, idx) => ({
      name: ing.name,
      quantity: Number(ing.quantity),
      unit: ing.unit,
      sortOrder: ing.sortOrder ?? idx,
    })),
    instructions: recipe.instructions.length > 0 ? [...recipe.instructions] : [''],
    nutrition: {
      caloriesKcal: rawNutrition.caloriesKcal ?? 0,
      proteinG: rawNutrition.proteinG ?? 0,
      carbsG: rawNutrition.carbsG ?? 0,
      fatG: rawNutrition.fatG ?? 0,
      sodiumMg: rawNutrition.sodiumMg ?? 0,
      potassiumMg: rawNutrition.potassiumMg ?? 0,
      fiberG: rawNutrition.fiberG ?? 0,
    },
  };
}

function StatusBadge({ status }: { status: RecipeStatus }): JSX.Element {
  return (
    <span
      className={
        status === 'published'
          ? 'inline-flex items-center rounded-full bg-green-100 px-2 py-0.5 text-xs font-medium text-green-800'
          : 'inline-flex items-center rounded-full bg-yellow-100 px-2 py-0.5 text-xs font-medium text-yellow-800'
      }
    >
      {status}
    </span>
  );
}

// ─── Multi-select tag component ───────────────────────────────────────────────

function TagMultiSelect({
  label,
  options,
  selected,
  onChange,
}: {
  label: string;
  options: string[];
  selected: string[];
  onChange: (tags: string[]) => void;
}): JSX.Element {
  function toggle(tag: string): void {
    if (selected.includes(tag)) {
      onChange(selected.filter((t) => t !== tag));
    } else {
      onChange([...selected, tag]);
    }
  }

  return (
    <div>
      <label className="block text-sm font-medium text-gray-700 mb-1">{label}</label>
      <div className="flex flex-wrap gap-2">
        {options.map((tag) => (
          <button
            key={tag}
            type="button"
            onClick={() => {
              toggle(tag);
            }}
            className={
              selected.includes(tag)
                ? 'px-2 py-1 text-xs rounded-full bg-blue-600 text-white border border-blue-600'
                : 'px-2 py-1 text-xs rounded-full bg-white text-gray-600 border border-gray-300 hover:border-blue-400'
            }
          >
            {tag}
          </button>
        ))}
      </div>
    </div>
  );
}

// ─── Ingredient repeater ──────────────────────────────────────────────────────

function IngredientRepeater({
  ingredients,
  onChange,
}: {
  ingredients: RecipeIngredientForm[];
  onChange: (ingredients: RecipeIngredientForm[]) => void;
}): JSX.Element {
  function updateIngredient(
    idx: number,
    field: keyof RecipeIngredientForm,
    value: string | number,
  ): void {
    const updated = ingredients.map((ing, i) => (i === idx ? { ...ing, [field]: value } : ing));
    onChange(updated);
  }

  function addIngredient(): void {
    onChange([...ingredients, { name: '', quantity: 1, unit: '', sortOrder: ingredients.length }]);
  }

  function removeIngredient(idx: number): void {
    onChange(ingredients.filter((_, i) => i !== idx).map((ing, i) => ({ ...ing, sortOrder: i })));
  }

  function moveIngredient(idx: number, direction: 'up' | 'down'): void {
    const targetIdx = direction === 'up' ? idx - 1 : idx + 1;
    if (targetIdx < 0 || targetIdx >= ingredients.length) return;
    const updated = [...ingredients];
    const temp = updated[idx]!;
    updated[idx] = { ...updated[targetIdx]!, sortOrder: idx };
    updated[targetIdx] = { ...temp, sortOrder: targetIdx };
    onChange(updated);
  }

  return (
    <div>
      <div className="flex items-center justify-between mb-2">
        <label className="block text-sm font-medium text-gray-700">Ingredients</label>
        <button
          type="button"
          onClick={addIngredient}
          className="text-xs text-blue-600 hover:text-blue-700"
        >
          + Add ingredient
        </button>
      </div>
      <div className="space-y-2">
        {ingredients.map((ing, idx) => (
          <div key={idx} className="flex gap-2 items-center">
            <div className="flex flex-col gap-0.5 shrink-0">
              <button
                type="button"
                onClick={() => {
                  moveIngredient(idx, 'up');
                }}
                disabled={idx === 0}
                className="text-gray-400 hover:text-gray-600 disabled:opacity-30 text-xs leading-none px-0.5"
                aria-label={`Move ingredient ${idx + 1} up`}
              >
                ▲
              </button>
              <button
                type="button"
                onClick={() => {
                  moveIngredient(idx, 'down');
                }}
                disabled={idx === ingredients.length - 1}
                className="text-gray-400 hover:text-gray-600 disabled:opacity-30 text-xs leading-none px-0.5"
                aria-label={`Move ingredient ${idx + 1} down`}
              >
                ▼
              </button>
            </div>
            <input
              type="text"
              placeholder="Name"
              value={ing.name}
              onChange={(e) => {
                updateIngredient(idx, 'name', e.target.value);
              }}
              className="flex-1 border border-gray-300 rounded px-2 py-1 text-sm"
              aria-label={`Ingredient ${idx + 1} name`}
            />
            <input
              type="number"
              placeholder="Qty"
              value={ing.quantity}
              min={0}
              onChange={(e) => {
                updateIngredient(idx, 'quantity', Number(e.target.value));
              }}
              className="w-20 border border-gray-300 rounded px-2 py-1 text-sm"
              aria-label={`Ingredient ${idx + 1} quantity`}
            />
            <input
              type="text"
              placeholder="Unit"
              value={ing.unit}
              onChange={(e) => {
                updateIngredient(idx, 'unit', e.target.value);
              }}
              className="w-20 border border-gray-300 rounded px-2 py-1 text-sm"
              aria-label={`Ingredient ${idx + 1} unit`}
            />
            <button
              type="button"
              onClick={() => {
                removeIngredient(idx);
              }}
              className="text-red-500 hover:text-red-700 text-xs px-1"
              aria-label={`Remove ingredient ${idx + 1}`}
            >
              ✕
            </button>
          </div>
        ))}
      </div>
    </div>
  );
}

// ─── Instruction step repeater ────────────────────────────────────────────────

function InstructionRepeater({
  instructions,
  onChange,
}: {
  instructions: string[];
  onChange: (steps: string[]) => void;
}): JSX.Element {
  function updateStep(idx: number, value: string): void {
    onChange(instructions.map((step, i) => (i === idx ? value : step)));
  }

  function addStep(): void {
    onChange([...instructions, '']);
  }

  function removeStep(idx: number): void {
    onChange(instructions.filter((_, i) => i !== idx));
  }

  function moveStep(idx: number, direction: 'up' | 'down'): void {
    const targetIdx = direction === 'up' ? idx - 1 : idx + 1;
    if (targetIdx < 0 || targetIdx >= instructions.length) return;
    const updated = [...instructions];
    const temp = updated[idx]!;
    updated[idx] = updated[targetIdx]!;
    updated[targetIdx] = temp;
    onChange(updated);
  }

  return (
    <div>
      <div className="flex items-center justify-between mb-2">
        <label className="block text-sm font-medium text-gray-700">Instructions</label>
        <button
          type="button"
          onClick={addStep}
          className="text-xs text-blue-600 hover:text-blue-700"
        >
          + Add step
        </button>
      </div>
      <div className="space-y-2">
        {instructions.map((step, idx) => (
          <div key={idx} className="flex gap-2 items-start">
            <div className="flex flex-col gap-0.5 shrink-0 pt-2">
              <button
                type="button"
                onClick={() => {
                  moveStep(idx, 'up');
                }}
                disabled={idx === 0}
                className="text-gray-400 hover:text-gray-600 disabled:opacity-30 text-xs leading-none px-0.5"
                aria-label={`Move step ${idx + 1} up`}
              >
                ▲
              </button>
              <button
                type="button"
                onClick={() => {
                  moveStep(idx, 'down');
                }}
                disabled={idx === instructions.length - 1}
                className="text-gray-400 hover:text-gray-600 disabled:opacity-30 text-xs leading-none px-0.5"
                aria-label={`Move step ${idx + 1} down`}
              >
                ▼
              </button>
            </div>
            <span className="text-xs text-gray-400 pt-2 w-5 shrink-0">{idx + 1}.</span>
            <textarea
              value={step}
              onChange={(e) => {
                updateStep(idx, e.target.value);
              }}
              rows={2}
              className="flex-1 border border-gray-300 rounded px-2 py-1 text-sm"
              aria-label={`Instruction step ${idx + 1}`}
            />
            <button
              type="button"
              onClick={() => {
                removeStep(idx);
              }}
              className="text-red-500 hover:text-red-700 text-xs px-1 pt-2"
              aria-label={`Remove step ${idx + 1}`}
            >
              ✕
            </button>
          </div>
        ))}
      </div>
    </div>
  );
}

// ─── Nutrition editor ─────────────────────────────────────────────────────────

function NutritionEditor({
  nutrition,
  onChange,
}: {
  nutrition: RecipeNutritionForm;
  onChange: (n: RecipeNutritionForm) => void;
}): JSX.Element {
  const fields: Array<{ key: keyof RecipeNutritionForm; label: string }> = [
    { key: 'caloriesKcal', label: 'Calories (kcal)' },
    { key: 'proteinG', label: 'Protein (g)' },
    { key: 'carbsG', label: 'Carbs (g)' },
    { key: 'fatG', label: 'Fat (g)' },
    { key: 'sodiumMg', label: 'Sodium (mg)' },
    { key: 'potassiumMg', label: 'Potassium (mg)' },
    { key: 'fiberG', label: 'Fiber (g)' },
  ];

  return (
    <div>
      <label className="block text-sm font-medium text-gray-700 mb-2">
        Nutrition (per serving)
      </label>
      <div className="grid grid-cols-2 gap-2 sm:grid-cols-3 lg:grid-cols-4">
        {fields.map(({ key, label }) => (
          <div key={key}>
            <label className="block text-xs text-gray-500 mb-1" htmlFor={`nutrition-${key}`}>
              {label}
            </label>
            <input
              id={`nutrition-${key}`}
              type="number"
              min={0}
              step={0.1}
              value={nutrition[key]}
              onChange={(e) => {
                onChange({ ...nutrition, [key]: Number(e.target.value) });
              }}
              className="w-full border border-gray-300 rounded px-2 py-1 text-sm"
            />
          </div>
        ))}
      </div>
    </div>
  );
}

// ─── Spanish dual sign-off dialog ─────────────────────────────────────────────

function SpanishSignOffDialog({
  onConfirm,
  onCancel,
}: {
  onConfirm: () => void;
  onCancel: () => void;
}): JSX.Element {
  return (
    <div
      className="fixed inset-0 bg-black/50 flex items-center justify-center z-50"
      role="dialog"
      aria-modal="true"
    >
      <div className="bg-white rounded-lg shadow-xl max-w-md w-full mx-4 p-6">
        <h2 className="text-lg font-semibold text-gray-900 mb-3">Spanish Dual Sign-Off Required</h2>
        <p className="text-sm text-gray-600 mb-4">
          Publishing a Spanish recipe requires dual sign-off from:
        </p>
        <ul className="text-sm text-gray-600 list-disc list-inside mb-6 space-y-1">
          <li>Certified medical translator</li>
          <li>Clinical Team member</li>
        </ul>
        <p className="text-sm font-medium text-gray-800 mb-6">
          By clicking Confirm, you attest that both parties have reviewed and approved this Spanish
          recipe translation.
        </p>
        <div className="flex gap-3 justify-end">
          <button
            type="button"
            onClick={onCancel}
            className="px-4 py-2 text-sm text-gray-700 border border-gray-300 rounded hover:bg-gray-50"
          >
            Cancel
          </button>
          <button
            type="button"
            onClick={onConfirm}
            className="px-4 py-2 text-sm text-white bg-blue-600 rounded hover:bg-blue-700"
          >
            Confirm Dual Sign-Off
          </button>
        </div>
      </div>
    </div>
  );
}

// ─── Recipe editor form ───────────────────────────────────────────────────────

function RecipeEditor({
  recipe,
  accessToken,
  onSaved,
  onCancel,
}: {
  recipe: RecipeDto | null; // null = create mode
  accessToken: string;
  onSaved: (saved: RecipeDto) => void;
  onCancel: () => void;
}): JSX.Element {
  const [form, setForm] = useState<RecipeFormState>(recipe ? recipeToForm(recipe) : emptyForm());
  const [saving, setSaving] = useState(false);
  const [saveError, setSaveError] = useState<string | null>(null);
  const [showSpanishDialog, setShowSpanishDialog] = useState(false);

  const isEditing = recipe !== null;

  async function handleSave(): Promise<void> {
    setSaving(true);
    setSaveError(null);

    try {
      const url = isEditing
        ? `${SERVICES_BASE_URL}/v1/core/recipes/${recipe.id}`
        : `${SERVICES_BASE_URL}/v1/core/recipes`;

      const method = isEditing ? 'PATCH' : 'POST';

      const body = buildRequestBody(form);

      const res = await fetch(url, {
        method,
        headers: {
          'Content-Type': 'application/json',
          Accept: 'application/json',
          Authorization: `Bearer ${accessToken}`,
        },
        body: JSON.stringify(body),
      });

      if (!res.ok) {
        const errText = await res.text();
        setSaveError(`Save failed (${res.status}): ${errText}`);
        return;
      }

      const saved = (await res.json()) as RecipeDto;
      onSaved(saved);
    } catch {
      setSaveError('Unable to connect to recipe API.');
    } finally {
      setSaving(false);
    }
  }

  async function handlePublish(): Promise<void> {
    if (form.language === 'es') {
      setShowSpanishDialog(true);
      return;
    }
    await doPublish(false);
  }

  async function handleSpanishConfirm(): Promise<void> {
    setShowSpanishDialog(false);
    await doPublish(true);
  }

  async function doPublish(spanishDualSignOff: boolean): Promise<void> {
    setSaving(true);
    setSaveError(null);

    try {
      const url =
        isEditing && recipe
          ? `${SERVICES_BASE_URL}/v1/core/recipes/${recipe.id}`
          : `${SERVICES_BASE_URL}/v1/core/recipes`;

      const method = isEditing ? 'PATCH' : 'POST';

      const body: Record<string, unknown> = {
        ...buildRequestBody(form),
        status: 'published',
        ...(spanishDualSignOff ? { spanishDualSignOff: true } : {}),
      };

      const res = await fetch(url, {
        method,
        headers: {
          'Content-Type': 'application/json',
          Accept: 'application/json',
          Authorization: `Bearer ${accessToken}`,
        },
        body: JSON.stringify(body),
      });

      if (!res.ok) {
        const errText = await res.text();
        setSaveError(`Publish failed (${res.status}): ${errText}`);
        return;
      }

      const saved = (await res.json()) as RecipeDto;
      onSaved(saved);
    } catch {
      setSaveError('Unable to connect to recipe API.');
    } finally {
      setSaving(false);
    }
  }

  function buildRequestBody(f: RecipeFormState): Record<string, unknown> {
    const body: Record<string, unknown> = {
      title: f.title,
      description: f.description || undefined,
      cuisine: f.cuisine || undefined,
      prep_time_min: f.prepTimeMin,
      cook_time_min: f.cookTimeMin,
      servings: f.servings,
      language: f.language,
      clinical_tags: f.clinicalTags,
      dietary_tags: f.dietaryTags,
      condition_tags: f.conditionTags,
      cultural_tags: f.culturalTags,
      ingredients: f.ingredients
        .filter((i) => i.name.trim())
        .map((i, idx) => ({
          name: i.name,
          quantity: i.quantity,
          unit: i.unit,
          sort_order: i.sortOrder ?? idx,
        })),
      instructions: f.instructions.filter((s) => s.trim()),
      nutrition: f.nutrition,
    };

    if (f.language === 'es' && f.parentRecipeId) {
      body['parent_recipe_id'] = f.parentRecipeId;
    }

    return body;
  }

  return (
    <>
      {showSpanishDialog && (
        <SpanishSignOffDialog
          onConfirm={() => {
            void handleSpanishConfirm();
          }}
          onCancel={() => {
            setShowSpanishDialog(false);
          }}
        />
      )}

      <div className="bg-white border border-gray-200 rounded-lg p-6 max-w-4xl">
        <div className="flex items-center justify-between mb-6">
          <h2 className="text-xl font-semibold text-gray-900">
            {isEditing ? 'Edit Recipe' : 'New Recipe'}
          </h2>
          <button
            type="button"
            onClick={onCancel}
            className="text-gray-400 hover:text-gray-600 text-sm"
          >
            ✕ Cancel
          </button>
        </div>

        {saveError !== null && (
          <div className="mb-4 p-3 bg-red-50 border border-red-200 rounded text-sm text-red-700">
            {saveError}
          </div>
        )}

        <div className="space-y-6">
          {/* Basic fields */}
          <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
            <div className="sm:col-span-2">
              <label
                htmlFor="recipe-title"
                className="block text-sm font-medium text-gray-700 mb-1"
              >
                Title <span className="text-red-500">*</span>
              </label>
              <input
                id="recipe-title"
                type="text"
                value={form.title}
                onChange={(e) => {
                  setForm({ ...form, title: e.target.value });
                }}
                className="w-full border border-gray-300 rounded px-3 py-2 text-sm"
                placeholder="Recipe title"
              />
            </div>

            <div className="sm:col-span-2">
              <label
                htmlFor="recipe-description"
                className="block text-sm font-medium text-gray-700 mb-1"
              >
                Description
              </label>
              <textarea
                id="recipe-description"
                value={form.description}
                onChange={(e) => {
                  setForm({ ...form, description: e.target.value });
                }}
                rows={3}
                className="w-full border border-gray-300 rounded px-3 py-2 text-sm"
                placeholder="Brief description"
              />
            </div>

            <div>
              <label
                htmlFor="recipe-cuisine"
                className="block text-sm font-medium text-gray-700 mb-1"
              >
                Cuisine
              </label>
              <input
                id="recipe-cuisine"
                type="text"
                value={form.cuisine}
                onChange={(e) => {
                  setForm({ ...form, cuisine: e.target.value });
                }}
                className="w-full border border-gray-300 rounded px-3 py-2 text-sm"
                placeholder="e.g. Mediterranean"
              />
            </div>

            <div>
              <label
                htmlFor="recipe-language"
                className="block text-sm font-medium text-gray-700 mb-1"
              >
                Language
              </label>
              <select
                id="recipe-language"
                value={form.language}
                onChange={(e) => {
                  setForm({ ...form, language: e.target.value as LanguageCode });
                }}
                className="w-full border border-gray-300 rounded px-3 py-2 text-sm"
              >
                <option value="en">English (en)</option>
                <option value="es">Spanish (es)</option>
              </select>
            </div>

            {form.language === 'es' && (
              <div className="sm:col-span-2">
                <label
                  htmlFor="recipe-parent"
                  className="block text-sm font-medium text-gray-700 mb-1"
                >
                  Parent Recipe ID <span className="text-gray-400">(English variant ULID)</span>
                </label>
                <input
                  id="recipe-parent"
                  type="text"
                  value={form.parentRecipeId}
                  onChange={(e) => {
                    setForm({ ...form, parentRecipeId: e.target.value });
                  }}
                  className="w-full border border-gray-300 rounded px-3 py-2 text-sm"
                  placeholder="ULID of the English parent recipe"
                />
              </div>
            )}

            <div>
              <label htmlFor="recipe-prep" className="block text-sm font-medium text-gray-700 mb-1">
                Prep Time (min)
              </label>
              <input
                id="recipe-prep"
                type="number"
                min={0}
                value={form.prepTimeMin}
                onChange={(e) => {
                  setForm({ ...form, prepTimeMin: Number(e.target.value) });
                }}
                className="w-full border border-gray-300 rounded px-3 py-2 text-sm"
              />
            </div>

            <div>
              <label htmlFor="recipe-cook" className="block text-sm font-medium text-gray-700 mb-1">
                Cook Time (min)
              </label>
              <input
                id="recipe-cook"
                type="number"
                min={0}
                value={form.cookTimeMin}
                onChange={(e) => {
                  setForm({ ...form, cookTimeMin: Number(e.target.value) });
                }}
                className="w-full border border-gray-300 rounded px-3 py-2 text-sm"
              />
            </div>

            <div>
              <label
                htmlFor="recipe-servings"
                className="block text-sm font-medium text-gray-700 mb-1"
              >
                Servings
              </label>
              <input
                id="recipe-servings"
                type="number"
                min={1}
                value={form.servings}
                onChange={(e) => {
                  setForm({ ...form, servings: Number(e.target.value) });
                }}
                className="w-full border border-gray-300 rounded px-3 py-2 text-sm"
              />
            </div>
          </div>

          {/* Clinical tags — mandatory */}
          <TagMultiSelect
            label="Clinical Track Tags (required — at least 1)"
            options={[...CLINICAL_TRACK_OPTIONS]}
            selected={form.clinicalTags}
            onChange={(tags) => {
              setForm({ ...form, clinicalTags: tags as ClinicalTrack[] });
            }}
          />

          {/* Dietary tags */}
          <TagMultiSelect
            label="Dietary Tags"
            options={DIETARY_TAG_OPTIONS}
            selected={form.dietaryTags}
            onChange={(tags) => {
              setForm({ ...form, dietaryTags: tags });
            }}
          />

          {/* Condition tags */}
          <TagMultiSelect
            label="Condition Tags"
            options={CONDITION_TAG_OPTIONS}
            selected={form.conditionTags}
            onChange={(tags) => {
              setForm({ ...form, conditionTags: tags });
            }}
          />

          {/* Cultural tags */}
          <TagMultiSelect
            label="Cultural Tags"
            options={CULTURAL_TAG_OPTIONS}
            selected={form.culturalTags}
            onChange={(tags) => {
              setForm({ ...form, culturalTags: tags });
            }}
          />

          {/* Ingredients */}
          <IngredientRepeater
            ingredients={form.ingredients}
            onChange={(ingredients) => {
              setForm({ ...form, ingredients });
            }}
          />

          {/* Instructions */}
          <InstructionRepeater
            instructions={form.instructions}
            onChange={(instructions) => {
              setForm({ ...form, instructions });
            }}
          />

          {/* Nutrition */}
          <NutritionEditor
            nutrition={form.nutrition}
            onChange={(nutrition) => {
              setForm({ ...form, nutrition });
            }}
          />
        </div>

        {/* Action buttons */}
        <div className="mt-8 flex items-center gap-3 border-t border-gray-100 pt-6">
          <button
            type="button"
            onClick={() => {
              void handleSave();
            }}
            disabled={saving || form.title.trim() === '' || form.clinicalTags.length === 0}
            className="px-4 py-2 text-sm text-white bg-blue-600 rounded hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed"
          >
            {saving ? 'Saving…' : 'Save Draft'}
          </button>

          <button
            type="button"
            onClick={() => {
              void handlePublish();
            }}
            disabled={saving || form.title.trim() === '' || form.clinicalTags.length === 0}
            className="px-4 py-2 text-sm text-white bg-green-600 rounded hover:bg-green-700 disabled:opacity-50 disabled:cursor-not-allowed"
          >
            {saving ? 'Publishing…' : 'Publish'}
          </button>

          <button
            type="button"
            onClick={onCancel}
            className="px-4 py-2 text-sm text-gray-700 border border-gray-300 rounded hover:bg-gray-50"
          >
            Cancel
          </button>
        </div>
      </div>
    </>
  );
}

// ─── Main page component ──────────────────────────────────────────────────────

export function RecipeCorpusPage(): JSX.Element {
  const auth = usePortalAuth();
  const [recipes, setRecipes] = useState<RecipeDto[]>([]);
  const [loading, setLoading] = useState(true);
  const [pageError, setPageError] = useState<string | null>(null);
  const [statusFilter, setStatusFilter] = useState<RecipeStatus | 'all'>('all');
  const [searchQuery, setSearchQuery] = useState('');
  // null = create mode; RecipeDto = edit mode. showEditor gates visibility.
  const [editingRecipe, setEditingRecipe] = useState<RecipeDto | null>(null);
  const [showEditor, setShowEditor] = useState(false);

  const accessToken = auth.user?.access_token ?? '';

  // Load recipes
  function loadRecipes(): void {
    const token = auth.user?.access_token;
    if (!token) {
      setPageError('Not authenticated — please sign in again.');
      setLoading(false);
      return;
    }

    setLoading(true);
    void fetch(`${SERVICES_BASE_URL}/v1/core/recipes`, {
      headers: {
        Accept: 'application/json',
        Authorization: `Bearer ${token}`,
      },
    })
      .then(async (res) => {
        if (!res.ok) {
          setPageError(`Failed to load recipes (${res.status})`);
          return;
        }
        const data = (await res.json()) as RecipeDto[];
        setRecipes(data);
        setPageError(null);
      })
      .catch(() => {
        setPageError('Unable to connect to recipe API.');
      })
      .finally(() => {
        setLoading(false);
      });
  }

  // Load on mount
  useEffect(() => {
    loadRecipes();
  }, []);

  // Filter recipes client-side
  const filteredRecipes = recipes.filter((r) => {
    const matchesStatus = statusFilter === 'all' || r.status === statusFilter;
    const q = searchQuery.toLowerCase().trim();
    const matchesSearch =
      q === '' ||
      r.title.toLowerCase().includes(q) ||
      r.clinicalTags.some((t) => t.toLowerCase().includes(q));
    return matchesStatus && matchesSearch;
  });

  function openNew(): void {
    setEditingRecipe(null);
    setShowEditor(true);
  }

  function openEdit(recipe: RecipeDto): void {
    setEditingRecipe(recipe);
    setShowEditor(true);
  }

  function handleSaved(saved: RecipeDto): void {
    setRecipes((prev) => {
      const idx = prev.findIndex((r) => r.id === saved.id);
      if (idx >= 0) {
        return prev.map((r) => (r.id === saved.id ? saved : r));
      }
      return [saved, ...prev];
    });
    setShowEditor(false);
  }

  function handleCancelEditor(): void {
    setShowEditor(false);
  }

  if (showEditor) {
    return (
      <main className="p-8">
        <RecipeEditor
          recipe={editingRecipe}
          accessToken={accessToken}
          onSaved={handleSaved}
          onCancel={handleCancelEditor}
        />
      </main>
    );
  }

  return (
    <main className="p-8">
      <div className="flex items-center justify-between mb-2">
        <h1 className="text-2xl font-semibold text-gray-900">Recipe Corpus</h1>
        <button
          type="button"
          onClick={openNew}
          className="px-4 py-2 text-sm text-white bg-blue-600 rounded hover:bg-blue-700"
        >
          New Recipe
        </button>
      </div>
      <p className="text-sm text-gray-500 mb-6">
        Manage eCKM and CKM clinical recipe corpus. Create, edit, and publish recipes for the Betty
        app.
      </p>

      {/* Filters */}
      <div className="flex gap-4 mb-4">
        <div>
          <label htmlFor="status-filter" className="text-sm text-gray-600 mr-2">
            Status
          </label>
          <select
            id="status-filter"
            value={statusFilter}
            onChange={(e) => {
              setStatusFilter(e.target.value as RecipeStatus | 'all');
            }}
            className="border border-gray-300 rounded px-2 py-1 text-sm"
          >
            <option value="all">All</option>
            <option value="draft">Draft</option>
            <option value="published">Published</option>
          </select>
        </div>

        <div>
          <input
            type="search"
            placeholder="Search by title or tag…"
            value={searchQuery}
            onChange={(e) => {
              setSearchQuery(e.target.value);
            }}
            className="border border-gray-300 rounded px-3 py-1 text-sm w-64"
            aria-label="Search recipes"
          />
        </div>
      </div>

      {pageError !== null && <p className="mt-6 text-red-600">{pageError}</p>}

      {pageError === null && (
        <div className="overflow-x-auto">
          {!loading && filteredRecipes.length === 0 ? (
            <p className="text-gray-500 py-8 text-center">
              {recipes.length === 0
                ? 'No recipes found. Click "New Recipe" to create the first one.'
                : 'No recipes match your filter.'}
            </p>
          ) : (
            <Table.ScrollArea loading={loading} loadingLabel="Loading recipes…">
              <Table.Root className="rounded-lg border border-gray-200">
                <Table.Header>
                  <Table.Row>
                    <Table.ColumnHeaderCell>Title</Table.ColumnHeaderCell>
                    <Table.ColumnHeaderCell>Clinical Tags</Table.ColumnHeaderCell>
                    <Table.ColumnHeaderCell>Language</Table.ColumnHeaderCell>
                    <Table.ColumnHeaderCell>Status</Table.ColumnHeaderCell>
                    <Table.ColumnHeaderCell>Actions</Table.ColumnHeaderCell>
                  </Table.Row>
                </Table.Header>
                <Table.Body>
                  {filteredRecipes.map((recipe) => (
                    <Table.Row key={recipe.id} className="hover:bg-gray-50">
                      <Table.Cell className="text-gray-900">{recipe.title}</Table.Cell>
                      <Table.Cell>
                        <span className="text-gray-600">{recipe.clinicalTags.join(', ')}</span>
                      </Table.Cell>
                      <Table.Cell className="text-gray-600">{recipe.language}</Table.Cell>
                      <Table.Cell>
                        <StatusBadge status={recipe.status} />
                      </Table.Cell>
                      <Table.Cell>
                        <button
                          type="button"
                          onClick={() => {
                            openEdit(recipe);
                          }}
                          className="text-sm font-medium text-blue-600 hover:text-blue-800"
                        >
                          Edit
                        </button>
                      </Table.Cell>
                    </Table.Row>
                  ))}
                </Table.Body>
              </Table.Root>
            </Table.ScrollArea>
          )}
        </div>
      )}
    </main>
  );
}
